import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Frame,
  Navigation,
  TopBar,
  Tabs,
  Page,
  SkeletonPage,
  SkeletonBodyText,
  Layout,
} from '@shopify/polaris';
import { HomeIcon, SettingsIcon } from '@shopify/polaris-icons';
import { NavMenu } from '@shopify/app-bridge-react';

interface NavigationItem {
  url: string;
  label: string;
  icon: typeof HomeIcon;
  matchPaths?: string[];
}

const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    url: '/',
    label: 'Dashboard',
    icon: HomeIcon,
    matchPaths: ['/'],
  },
  {
    url: '/settings',
    label: 'Settings',
    icon: SettingsIcon,
    matchPaths: ['/settings'],
  },
];

function LoadingState() {
  return (
    <SkeletonPage primaryAction>
      <Layout>
        <Layout.Section>
          <SkeletonBodyText lines={6} />
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );
}

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isAppBridgeReady, setIsAppBridgeReady] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    // App Bridge v4 initializes automatically via the CDN script.
    // We check if the shopify global is available.
    const checkAppBridge = () => {
      if (typeof window !== 'undefined' && (window as unknown as { shopify?: unknown }).shopify) {
        setIsAppBridgeReady(true);
        // Check if we're embedded in Shopify Admin (has 'host' param or in iframe)
        const urlParams = new URLSearchParams(window.location.search);
        const hasHost = urlParams.has('host');
        const inIframe = window.self !== window.top;
        setIsEmbedded(hasHost || inIframe);
      } else {
        // In development outside Shopify admin, proceed anyway
        // The app will work but without App Bridge features
        setTimeout(() => setIsAppBridgeReady(true), 100);
      }
    };

    checkAppBridge();
  }, []);

  const toggleMobileNavigationActive = useCallback(() => {
    setMobileNavigationActive((active) => !active);
  }, []);

  const handleNavigationClick = useCallback(
    (url: string) => {
      navigate(url);
      setMobileNavigationActive(false);
    },
    [navigate]
  );

  const isSelected = (item: NavigationItem): boolean => {
    if (item.matchPaths) {
      return item.matchPaths.includes(location.pathname);
    }
    return location.pathname === item.url;
  };

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={NAVIGATION_ITEMS.map((item) => ({
          url: item.url,
          label: item.label,
          icon: item.icon,
          selected: isSelected(item),
          onClick: () => handleNavigationClick(item.url),
        }))}
      />
    </Navigation>
  );

  const logoMarkup = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        paddingLeft: 'var(--p-space-400)',
      }}
    >
      <span
        style={{
          fontWeight: 'var(--p-font-weight-semibold)',
          fontSize: 'var(--p-font-size-350)',
          color: 'var(--p-color-text)',
        }}
      >
        Pingdex
      </span>
    </div>
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={toggleMobileNavigationActive}
      contextControl={logoMarkup}
    />
  );

  // App Bridge NavigationMenu for Shopify Admin sidebar
  const appBridgeNavMenuMarkup = isAppBridgeReady ? (
    <NavMenu>
      <a href="/" rel="home">Dashboard</a>
      <a href="/settings">Settings</a>
    </NavMenu>
  ) : null;

  if (!isAppBridgeReady) {
    return (
      <Frame
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        <LoadingState />
      </Frame>
    );
  }

  // When embedded in Shopify Admin, use Tabs for navigation instead of sidebar
  if (isEmbedded) {
    const tabs = [
      {
        id: 'dashboard',
        content: 'Dashboard',
        url: '/',
      },
      {
        id: 'settings',
        content: 'Settings',
        url: '/settings',
      },
    ];

    const selectedTabIndex = location.pathname === '/settings' ? 1 : 0;

    const handleTabChange = (selectedIndex: number) => {
      navigate(tabs[selectedIndex].url);
    };

    return (
      <>
        {appBridgeNavMenuMarkup}
        <Page>
          <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange}>
            <div style={{ paddingTop: 'var(--p-space-400)' }}>
              <Outlet />
            </div>
          </Tabs>
        </Page>
      </>
    );
  }

  // Standalone mode (development or direct access) - render full Frame
  return (
    <>
      {appBridgeNavMenuMarkup}
      <Frame
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        <Outlet />
      </Frame>
    </>
  );
}
